import { useCallback } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import type { TextAlignment } from '../types'
import { AVAILABLE_ALIGNMENTS } from '../constants'
import { AlignmentLeftIcon } from './icons/AlignmentLeftIcon'
import { AlignmentCenterIcon } from './icons/AlignmentCenterIcon'
import { AlignmentRightIcon } from './icons/AlignmentRightIcon'
import classes from './AlignmentButton.module.scss'

/**
 * Пропсы для компонента AlignmentButton
 */
export interface AlignmentButtonProps {
    /**
     * Текущее выравнивание текста
     */
    value: TextAlignment
    /**
     * Обработчик изменения выравнивания
     */
    onChange?: (alignment: TextAlignment) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Отключена ли кнопка
     */
    disabled?: boolean
}

/**
 * Компонент кнопки-переключателя выравнивания текста
 * Переключает выравнивание по кругу: left → center → right → left
 */
export function AlignmentButton({
    value,
    onChange,
    className,
    disabled = false,
}: AlignmentButtonProps) {
    /**
     * Обработчик клика по кнопке
     * Переключает выравнивание на следующее в цикле
     */
    const handleClick = useCallback(() => {
        if (disabled || !onChange) {
            return
        }

        const currentIndex = AVAILABLE_ALIGNMENTS.indexOf(value)
        const nextIndex = (currentIndex + 1) % AVAILABLE_ALIGNMENTS.length
        const nextAlignment = AVAILABLE_ALIGNMENTS[nextIndex]
        onChange(nextAlignment)
    }, [value, onChange, disabled])

    /**
     * Получить иконку для текущего выравнивания
     */
    const getIcon = useCallback(() => {
        switch (value) {
            case 'left':
                return <AlignmentLeftIcon />
            case 'center':
                return <AlignmentCenterIcon />
            case 'right':
                return <AlignmentRightIcon />
            default:
                return <AlignmentCenterIcon />
        }
    }, [value])

    return (
        <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    className={cn(classes.button, className, {
                        [classes.buttonDisabled]: disabled,
                    })}
                    onClick={handleClick}
                    disabled={disabled}
                    aria-label="Выбрать выравнивание"
                    aria-pressed={!disabled}
                >
                    {getIcon()}
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="z-[60]">
                Выравнивание
            </TooltipContent>
        </Tooltip>
    )
}
